import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, CheckCircle2, ExternalLink, Download } from "lucide-react";
import { CURRENT_ISSUE } from "@/data/newsletterIssues";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Newsletter signup modal.
 * Collects: firstName + email + explicit unchecked consent checkbox.
 * On success: shows success state + Read Web Edition + Download PDF buttons.
 */
export function NewsletterSignupModal({ open, onClose }) {
  const [firstName, setFirstName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [consent, setConsent] = React.useState(false); // unchecked by default per spec
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [issue, setIssue] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      const t = setTimeout(() => {
        setFirstName("");
        setEmail("");
        setConsent(false);
        setLoading(false);
        setDone(false);
        setIssue(null);
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    if (!consent) {
      toast.error("Please confirm consent so we can send you the free issue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim(),
        consent: true,
      });
      setIssue(data?.issue || CURRENT_ISSUE);
      setDone(true);
      toast.success("You're on the list — Issue 01 is ready.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again in a moment.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-signup-title"
      data-testid="newsletter-signup-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#050912]/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[#142339] border border-[#D4AF37]/40 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close signup form"
          data-testid="newsletter-signup-close"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        {!done ? (
          <form onSubmit={handleSubmit} className="p-8 sm:p-10">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">
              Free · Weekly · No spam
            </div>
            <h2
              id="newsletter-signup-title"
              className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mb-3"
            >
              Send Me the Free Issue
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              Get instant access to the current OGO Weekly Notary Brief.
              We&apos;ll email future issues so you stay informed.
            </p>

            <label
              htmlFor="nl-first-name"
              className="block text-[10px] uppercase tracking-[0.22em] text-slate-300 mb-2"
            >
              First name
            </label>
            <input
              id="nl-first-name"
              name="firstName"
              type="text"
              required
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              data-testid="newsletter-first-name-input"
              className="w-full bg-[#0B1120] border border-white/15 focus:border-[#D4AF37] outline-none text-white px-4 py-3 text-sm mb-5 transition-colors"
            />

            <label
              htmlFor="nl-email"
              className="block text-[10px] uppercase tracking-[0.22em] text-slate-300 mb-2"
            >
              Email address
            </label>
            <input
              id="nl-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email-input"
              className="w-full bg-[#0B1120] border border-white/15 focus:border-[#D4AF37] outline-none text-white px-4 py-3 text-sm mb-5 transition-colors"
            />

            <label className="flex items-start gap-3 mb-6 cursor-pointer group">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid="newsletter-consent-checkbox"
                className="mt-1 w-4 h-4 accent-[#D4AF37] shrink-0 cursor-pointer"
              />
              <span className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                Yes, send me the OGO Weekly Notary Brief, new resources, and
                occasional OGO Notary updates. I understand that I may
                unsubscribe at any time.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] disabled:opacity-60 disabled:cursor-not-allowed px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors"
            >
              <Send className="w-4 h-4" />
              {loading ? "Sending…" : "Send Me the Free Issue"}
            </button>

            <p className="mt-4 text-[10px] uppercase tracking-[0.22em] text-slate-500 text-center">
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D4AF37]"
              >
                View our privacy policy
              </a>
            </p>
          </form>
        ) : (
          <div className="p-8 sm:p-10" data-testid="newsletter-signup-success">
            <div className="w-14 h-14 border-2 border-[#D4AF37] flex items-center justify-center text-[#D4AF37] mb-5">
              <CheckCircle2 className="w-7 h-7" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold mb-3">
              You&apos;re on the list
            </div>
            <h2 className="font-serif-display text-2xl sm:text-3xl text-white leading-tight mb-3">
              Issue {issue?.number || "01"} is ready.
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-8">
              Thank you, {firstName}. We&apos;ve added your email to the OGO
              Weekly Notary Brief list. Open the current issue now, or download
              the printable PDF.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`/newsletter/${issue?.slug || "issue-01"}`}
                data-testid="newsletter-success-read-web"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#142339] hover:bg-[#FDE047] px-5 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Read Web Edition
              </a>
              <a
                href={issue?.pdfUrl || CURRENT_ISSUE.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="newsletter-success-download-pdf"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#FDE047] px-5 py-3.5 text-[11px] uppercase tracking-[0.22em] font-bold transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download PDF
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
